import { Nullable } from "./../types/common";
import request from "../libs/request";

import {
  FileInfo,
  EvacueeCountRequest,
  EvacueeListRequest,
  EvacueeListType,
  EvacueeRequest,
  EvacueeStatusRequest,
  SituationStatusType,
  EvacuationUserAddition,
} from "../types/emergency";

import { PageableResponse } from "./talkServices";

const baseUrl = `${process.env.REACT_APP_API_URL}/v2/manager/evacuation/evacuee`;
const managerUrl = `${process.env.REACT_APP_API_URL}/v2/manager/evacuation/situation`;
//  /v2/manager/evacuation/situation/{evacuationId}/evacuees/addition
 
export type SituationItem = {
  evacuationId: string;
  evacuationName: string;
  evacuationCategoryCode: string;
  evacuationCategoryName: string;
  evacuationGroupId: string;
  evacuationGroupName: string;
  status: SituationStatusType;
  incidentAddress: string;
  incidentBuildingId: string;
  incidentBuildingName: string;
  evacueeCount: number;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  modifiedBy: number;
  modifiedByName: string;
  modifiedAt: string;
  countByStatus: SituationCountsType;
};

export type SituationCountsType = {
  createNoResponseCount: number; // 무응답
  evacuationCompleteCount: number; // 대피완료
  helpMeCount: number; // 구조요청
  onLeaveOrOUtCount: number; // 휴가/외근
  totalCount: number; // 총
  evacuationGroupId: string;
  evacuationId: string;
};

export type SituationListSearchType = {
  evacuationName: string;
  createdByName: string;
  evacuationCategoryCode: string;
  status: SituationStatusType;
  createdAtStart: string; // (yyyymmdd)
  createdAtEnd: string; // (yyyymmdd)
};

// 인자는 사실상 같으나, 빌딩id(향후) 를 제외 하고는 사용하진 않음.
export type SituationCountSearchType = {
  evacuationName: string;
  createdByName: string;
  evacuationCategoryCode: string;
  status: SituationStatusType;
  createdAtStart: string; // (yyyymmdd)
  createdAtEnd: string; // (yyyymmdd)
};

export type SituationStatusCount = {
  totalCreated: number;
  totalInProgress: number;
  totalEnded: number;
  totalAll: number;
};

export type ShelterItem = {
  isDefaultShelter: boolean;
  shelterId: string;
  shelterImg: {
    items: FileInfo[];
  };
  shelterName: string;
};

export type SituationDetailItem = {
  evacuationGroupId: string;
  evacuationGroupName: string;
  evacuationId: string;
  evacuationName: string;
  evacuationCategoryCode: string;
  evacuationCategoryName: string;
  incidentAddress: string;
  incidentBuildingId: string;
  incidentFloor: string;
  incidentFloorName: string;
  status: SituationStatusType;
  startedAt: string;
  endedAt: string;
  shelters: ShelterItem[];
};

export type EvacueeListCount = {
  evacuationGroupId: string;
  evacuationId: string;
  totalCount: number;
  createNoResponseCount: number;
  evacuationCompleteCount: number;
  onLeaveOrOUtCount: number;
  helpMeCount: number;
};

export type EvacuationBuildingCompany = {
  companyId: Nullable<string>;
  companyName: Nullable<string>;
  shelterId: Nullable<string>;
  shelterName: Nullable<string>;
  companyIsActive: Nullable<boolean>;
};

export type EvacuationGroupBuilding = {
  buildingId: Nullable<string>;
  buildingName: Nullable<string>;
  defaultShelterId: Nullable<string>;
  defaultShelterName: Nullable<string>;
  companyShelterMapDtoList: EvacuationBuildingCompany[];
};

export type EvacuationGroup = {
  evacuationGroupId: Nullable<string>;
  evacuationGroupName: Nullable<string>;
  situationCount: Nullable<number>;
  lat: Nullable<number>;
  lng: Nullable<number>;
  buildingList: EvacuationGroupBuilding[];
  createdBy: Nullable<number>;
  createdAt: Nullable<string>;
  modifiedBy: Nullable<number>;
  modifiedAt: Nullable<string>;
  active: Nullable<boolean>;
};

// 비상대피 관리
// TODO : 건물id 로 가져오도록 변경 필요
export const getSituationList = (params: Partial<SituationListSearchType>) =>
  request.get<PageableResponse<SituationItem>>(managerUrl + "/list-page", {
    params,
  });

// 상황 상태별 카운트
export const getSituationCount = (params: Partial<SituationCountSearchType>) =>
  request.get<SituationStatusCount>(managerUrl + "/list-status", {
    params,
  });

export const getSituationDetail = (evacuationId: string) =>
  request.get<SituationDetailItem>(managerUrl + `/${evacuationId}`);

// 상황 시작 처리
export const putSituationStart = (evacuationId: string) =>
  request.put(managerUrl + `/${evacuationId}/start`);

// 상황 종료 처리
export const putSituationEnd = (evacuationId: string) =>
  request.put(managerUrl + `/${evacuationId}/end`);

// 비상대피 참여자 목록
export const getEvacueeList = (params: EvacueeListRequest) =>
  request.get<EvacueeListType>(baseUrl + "/list-page", { params });
// 비상대피 참여자 목록 카운트
export const getEvacueeListCount = (params: EvacueeCountRequest) =>
  request.get<EvacueeListCount>(baseUrl + "/list-status-count", { params });

export const getEvacuationGroupBuildingList = (groupId: string) =>
  request.get<EvacuationGroup>(managerUrl + `/group/${groupId}`);

// 비상대피 참여자 수기 등록
export const postEvacuee = (params: EvacueeRequest) =>
  request.post(baseUrl, params);

// 2025-05-15 famous추가
export const postEvacueeAddition = (situationId: string, data: Partial<EvacuationUserAddition>[]) =>
  request.post(managerUrl + `/${situationId}/evacuees/addition`, data);

// 비상대피 참여자 상태 수정 (관리자 only)
export const patchEvacueeStatus = (
  evacuationEvacueeMapUuid: string,
  params: EvacueeStatusRequest
) => request.patch(baseUrl + `/${evacuationEvacueeMapUuid}/status`, params);

// 비상대피 참여자 삭제 (수기등록한 경우)
export const deleteEvacuee = (evacuationEvacueeMapUuid: string) =>
  request.delete(baseUrl + `/${evacuationEvacueeMapUuid}`);
